// src/components/auth/Register.js
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useRedirectIfAuthenticated } from '../../hooks/useRedirectIfAuthenticated';
import RegisterView from '../auth-v2/RegisterView';
import EmailVerificationPending from '../auth-v2/EmailVerificationPending';
import {
  normalizeRegistrationEmail, validateRegistrationEmail,
  validateRegistrationPassword, registrationErrorMessage,
} from '../../utils/registrationValidation';

const computePasswordRequirements = (password) => ({
  length: password.length >= 8,
  upperCase: /[A-Z]/.test(password),
  lowerCase: /[a-z]/.test(password),
  numbers: /\d/.test(password),
  specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password),
});

export default function Register() {
  const { signup, loginWithGoogle, currentUser } = useAuth();
  const navigate = useNavigate();

  // step: 'account' (name + email + Google) | 'security' (password) |
  // 'verify-email' (P-AUTH-EMAIL-VERIFY-2: password signup only — session
  // exists but Firebase hasn't confirmed the address yet) | 'success'
  // (shown once signup()/loginWithGoogle() has resolved AND, for the
  // password path, verification has been confirmed).
  const [step, setStep] = useState('account');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});
  const [topError, setTopError] = useState('');
  const [emailInUse, setEmailInUse] = useState(false);
  const [loading, setLoading] = useState(false);
  // P-AUTH-EMAIL-UX-1: true only when the success step was reached by
  // CONFIRMING the verification e-mail (password path) — lets the success
  // screen add its restrained "Adresse vérifiée." note. The Google path
  // never sets it, so that screen stays exactly as before.
  const [emailJustVerified, setEmailJustVerified] = useState(false);

  // P-AUTH-HOTFIX-2B: recovers a Google sign-in that completed via a
  // full-page redirect (the mobile popup-blocked fallback) — that reloads
  // the app fresh on /register with the session already restored by
  // AuthContext, but nothing was navigating away from this screen. Held off
  // on the 'success' step so an in-page (non-reloaded) sign-in still gets
  // to show its own "Bienvenue sur NSARA" screen before the person moves on.
  // Also held off on 'verify-email' (P-AUTH-EMAIL-VERIFY-2): the hook
  // itself already refuses to fire for an unverified currentUser, but
  // suppressing it here too keeps this page fully in charge of its own
  // pending→verified transition without any risk of a stray redirect
  // attempt in between.
  useRedirectIfAuthenticated(step === 'success' || step === 'verify-email');

  // Once Firebase reports the address as verified (via the shared
  // EmailVerificationPending screen below, rendered while step is
  // 'verify-email'), move on to the real "your account is ready" step —
  // it wasn't truthfully ready until this point.
  useEffect(() => {
    if (step === 'verify-email' && currentUser?.emailVerified) {
      setEmailJustVerified(true);
      setStep('success');
    }
  }, [step, currentUser?.emailVerified]);


  // Move focus to the new step's heading on transition (not on first mount,
  // so the page doesn't steal initial focus from the browser chrome).
  const headingRef = useRef(null);
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) { isFirstRender.current = false; return; }
    headingRef.current?.focus();
  }, [step]);

  const passwordRequirements = computePasswordRequirements(password);
  const passwordScore = Object.values(passwordRequirements).filter(Boolean).length;

  const clearFieldError = (field) => {
    setErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
  };

  const onNameChange = (event) => { setName(event.target.value); clearFieldError('name'); };
  const onEmailChange = (event) => {
    setEmail(event.target.value);
    clearFieldError('email');
    setEmailInUse(false);
    setTopError('');
  };
  const onPasswordChange = (event) => { setPassword(event.target.value); clearFieldError('password'); };
  const onConfirmPasswordChange = (event) => { setConfirmPassword(event.target.value); clearFieldError('confirmPassword'); };

  const onContinueToSecurity = (event) => {
    event.preventDefault();
    const newErrors = {};
    if (!name.trim()) newErrors.name = 'Le nom est requis';
    const emailError = validateRegistrationEmail(email);
    if (emailError) newErrors.email = emailError;
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;
    setEmail(normalizeRegistrationEmail(email));
    setStep('security');
  };

  // Back preserves name/email: they live in this component's state, not in
  // AccountStep, so simply re-rendering that step keeps the values intact.
  const onBackToAccount = () => {
    setErrors({});
    setTopError('');
    setEmailInUse(false);
    setStep('account');
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;
    const newErrors = {};
    const passwordError = validateRegistrationPassword(password);
    if (passwordError) newErrors.password = passwordError;
    if (password !== confirmPassword) newErrors.confirmPassword = 'Les mots de passe ne correspondent pas';
    setErrors(newErrors);
    if (Object.keys(newErrors).length) return;

    setLoading(true);
    setTopError('');
    setEmailInUse(false);
    try {
      await signup({ email: normalizeRegistrationEmail(email), password, name });
      // P-AUTH-EMAIL-VERIFY-2: a password account is never "ready" the
      // instant it's created — see supabase.js's signUp, which now also
      // requests Firebase's verification e-mail. The useEffect above
      // advances this to 'success' once currentUser.emailVerified is true.
      setStep('verify-email');
    } catch (error) {
      const code = `${error?.code || ''} ${error?.message || ''}`.toUpperCase();
      setEmailInUse(code.includes('EMAIL_EXISTS') || code.includes('EMAIL_ALREADY_IN_USE'));
      setTopError(registrationErrorMessage(error));
    } finally {
      setLoading(false);
    }
  };

  const onGoogleRegister = async () => {
    if (loading) return;
    setLoading(true);
    setTopError('');
    setEmailInUse(false);
    try {
      await loginWithGoogle();
      setStep('success');
    } catch (error) {
      setTopError('Impossible de continuer avec Google. Veuillez réessayer.');
    } finally {
      setLoading(false);
    }
  };

  const onGoToDashboard = () => navigate('/dashboard');

  if (step === 'verify-email') {
    return <EmailVerificationPending onSignedOut={() => setStep('account')} />;
  }

  return (
    <RegisterView
      step={step}
      headingRef={headingRef}
      name={name}
      email={email}
      password={password}
      confirmPassword={confirmPassword}
      errors={errors}
      topError={topError}
      emailInUse={emailInUse}
      loading={loading}
      passwordRequirements={passwordRequirements}
      passwordScore={passwordScore}
      onNameChange={onNameChange}
      onEmailChange={onEmailChange}
      onPasswordChange={onPasswordChange}
      onConfirmPasswordChange={onConfirmPasswordChange}
      onGoogleRegister={onGoogleRegister}
      onContinueToSecurity={onContinueToSecurity}
      onBackToAccount={onBackToAccount}
      onSubmit={onSubmit}
      onGoToDashboard={onGoToDashboard}
      verifiedNote={emailJustVerified}
    />
  );
}
